import React from "react";

/**
 * DateDisplay
 *
 * Shows:
 *  - Day of week (top)
 *  - Big day-of-month number (center-left)
 *  - Month (3 letters) + Year stacked on the right
 *
 * Props:
 *  - date: Date | string | number (defaults to today)
 *  - size: "sm" | "md" | "lg" (visual scale)
 *  - className: optional additional classes
 *  - onClick: optional click handler for making it interactive
 */

type Size = "sm" | "md" | "lg";

export interface DateDisplayProps {
  date?: Date | string | number;
  size?: Size;
  className?: string;
  onClick?: () => void;
}

function toDate(d?: Date | string | number) {
  if (!d) return new Date();
  return d instanceof Date ? d : new Date(d);
}

function parts(d: Date) {
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(d).slice(0, 3).toUpperCase();
  const day = new Intl.NumberFormat(undefined, { minimumIntegerDigits: 1 }).format(d.getDate());
  const month = new Intl.DateTimeFormat(undefined, { month: "short" }).format(d).slice(0, 3).toUpperCase();
  const year = d.getFullYear().toString();
  return { weekday, day, month, year };
}

const sizeMap: Record<Size, { weekday: string; day: string; month: string; year: string; gap: string; padding: string }> = {
  sm: { weekday: "text-xs", day: "text-4xl", month: "text-sm", year: "text-[10px]", gap: "gap-1", padding: "p-3" },
  md: { weekday: "text-sm", day: "text-6xl", month: "text-base", year: "text-xs", gap: "gap-1.5", padding: "p-4" },
  lg: { weekday: "text-base", day: "text-7xl", month: "text-lg", year: "text-sm", gap: "gap-2", padding: "p-5" },
};

export default function DateDisplay({ date, size = "md", className = "", onClick }: DateDisplayProps) {
  const d = toDate(date);
  const { weekday, day, month, year } = parts(d);
  const s = sizeMap[size];

  return (
    <div
      className={[
        "inline-flex rounded-2xl border border-gray-200 bg-white shadow-sm",
        s.padding,
        onClick ? "cursor-pointer hover:border-gray-300 transition-colors" : "",
        className,
      ].join(" ")}
      role="group"
      aria-label={`Date: ${weekday}, ${month} ${day}, ${year}`}
      onClick={onClick}
    >
      <div className="flex flex-col items-start">
        <div className={["uppercase tracking-wide text-gray-500", s.weekday].join(" ")}>{weekday}</div>
        <div className={["flex items-end", s.gap].join(" ")}>
          <div className={["leading-none font-semibold tabular-nums", s.day].join(" ")}>{day}</div>
          <div className="flex flex-col leading-none text-gray-700 select-none">
            <span className={["font-semibold", s.month].join(" ")}>{month}</span>
            <span className={s.year}>{year}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
